import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ShieldCheck, Heartbeat, LockKey, Gear, GitBranch, Plus, TrashSimple } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { dmsApi } from './dmsApi';
import BulkOcrCard from './BulkOcrCard';
import IngestQueueCard from './IngestQueueCard';
import NavVisibilityCard from './NavVisibilityCard';
import { formatJST } from '@/utils/datetime';

const PHASE_LABEL = { 1: 'Phase 1 — 基盤', 2: 'Phase 2 — デスクUX', 3: 'Phase 3 — ワークフロー', 4: 'Phase 4 — 連携', 5: 'Phase 5 — OCR・AI' };

/** ファイル統合（Step 2）— モジュール添付をドキュメントアプリへ取り込む。 */
function AdoptionCard() {
  const [status, setStatus] = useState(null);
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { setStatus(await dmsApi.adoptionStatus()); } catch { setStatus(null); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const run = async (dryRun) => {
    setBusy(true);
    try {
      const r = await dmsApi.adoptionRun(dryRun);
      setReport(r);
      toast.success(dryRun
        ? `取り込み対象 ${r.documents_pending}件（プレビュー・未実行）`
        : `${r.documents_created}件をドキュメントとして統合しました`);
      if (!dryRun) await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '実行に失敗しました');
    } finally { setBusy(false); }
  };

  if (!status) return null;
  const pending = status.stores.reduce((a, s) => a + s.pending, 0);

  return (
    <div className="rounded-xl border border-zinc-200 bg-white shadow-sm p-4" data-testid="dms-adoption-card">
      <p className="text-sm font-bold text-zinc-800 flex items-center gap-1.5 mb-1">
        <GitBranch size={16} weight="duotone" className="text-teal-600" /> ファイル統合（モジュール添付の取り込み）
      </p>
      <p className="text-[11px] text-zinc-400 mb-3">
        各モジュールの添付ファイルをドキュメントとして登録し、関係サービス（record_links）でレコードに紐付けます。
        元の行は削除せず「参照専用ポインタ」として残ります。新規アップロードは常にドキュメントアプリに保存されます。
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
        {status.stores.map((s) => (
          <div key={s.store} className={`rounded-lg border p-2.5 ${s.pending > 0 ? 'border-amber-200 bg-amber-50' : 'border-zinc-100 bg-zinc-50'}`}
            data-testid={`dms-adoption-store-${s.store}`}>
            <p className="text-[11px] font-semibold text-zinc-700">{s.label}</p>
            <p className="text-lg font-bold tabular-nums text-zinc-800">{s.adopted}<span className="text-xs text-zinc-400"> / {s.total}</span></p>
            <p className="text-[10px] text-zinc-500">
              {s.pending > 0 ? `未統合 ${s.pending}件` : '統合済み'}
              {s.skippable > 0 ? ` ・ 対象外（削除済み）${s.skippable}件` : ''}
            </p>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <Button size="sm" variant="outline" disabled={busy} onClick={() => run(true)}
          className="h-9 text-xs" data-testid="dms-adoption-dryrun">プレビュー（実行しない）</Button>
        <Button size="sm" disabled={busy || pending === 0} onClick={() => run(false)}
          className="h-9 text-xs bg-teal-700 hover:bg-teal-800" data-testid="dms-adoption-run">
          統合を実行{pending > 0 ? `（${pending}件）` : ''}
        </Button>
        <span className="text-[11px] text-zinc-400">統合済みドキュメント: {status.documents_adopted}件</span>
      </div>
      {report && (
        <div className="mt-3 rounded-lg border border-zinc-100 bg-zinc-50 p-2.5 space-y-1" data-testid="dms-adoption-report">
          <p className="text-[11px] font-semibold text-zinc-600">{report.dry_run ? 'プレビュー結果' : '実行結果'}</p>
          {report.stores.map((s) => (
            <p key={s.store} className="text-[11px] text-zinc-600">
              {s.label}: 対象 {s.total} / 取り込み {s.adopted} / 既存 {s.already}
              {s.unlinked > 0 ? ` / リンク先なし ${s.unlinked}` : ''}
              {s.skipped_count > 0 ? ` / スキップ ${s.skipped_count}` : ''}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}


/** 管理 — Feature Registry, dependency-safe toggles, health, trash retention. */
export default function DmsAdminPanel() {
  const [features, setFeatures] = useState([]);
  const [health, setHealth] = useState(null);
  const [retention, setRetention] = useState('90');
  const [maxConc, setMaxConc] = useState('2');
  const [perUser, setPerUser] = useState('1');
  const [queueTimeout, setQueueTimeout] = useState('30');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [f, h, s] = await Promise.all([dmsApi.features(), dmsApi.health(), dmsApi.settings()]);
      setFeatures(f.features || []);
      setHealth(h);
      setRetention(String(s.trash_retention_days));
      setMaxConc(String(s.ingest_max_concurrent ?? 2));
      setPerUser(String(s.ingest_per_user_limit ?? 1));
      setQueueTimeout(String(s.ingest_queue_timeout_min ?? 30));
    } catch (e) {
      toast.error(e?.response?.data?.detail || '管理情報の取得に失敗しました');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (feat, enabled) => {
    try {
      await dmsApi.toggleFeature(feat.id, enabled);
      toast.success(`「${feat.feature_name}」を${enabled ? '有効化' : '無効化'}しました`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '切替に失敗しました');
    }
  };

  const saveRetention = async () => {
    try {
      await dmsApi.saveSettings({
        trash_retention_days: Number(retention),
        ingest_max_concurrent: Number(maxConc),
        ingest_per_user_limit: Number(perUser),
        ingest_queue_timeout_min: Number(queueTimeout),
      });
      toast.success('ドキュメント設定を保存しました');
    } catch (e) { toast.error(e?.response?.data?.detail || '保存に失敗しました'); }
  };

  const purge = async () => {
    try {
      const r = await dmsApi.purgeExpired();
      toast.success(`保持期間超過の書類を${r.purged}件 完全削除しました`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || '実行に失敗しました'); }
  };

  if (loading) return <p className="py-16 text-center text-sm text-zinc-400">読み込み中...</p>;

  const phases = [...new Set(features.map((f) => f.phase))].sort();

  return (
    <div className="max-w-4xl space-y-5" data-testid="dms-admin-panel">
      {/* Health */}
      <div className="rounded-xl border border-zinc-200 bg-white shadow-sm p-4" data-testid="dms-admin-health">
        <p className="text-sm font-bold text-zinc-800 flex items-center gap-1.5 mb-3">
          <Heartbeat size={16} weight="duotone" className="text-rose-500" /> ヘルスチェック
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
          {[
            ['書類数', health.documents_total], ['ゴミ箱', health.documents_deleted],
            ['アーカイブ', health.documents_archived], ['総バージョン', health.versions_total],
            ['孤立バージョン', health.orphan_versions, health.orphan_versions > 0],
            ['破損リンク', health.broken_links, health.broken_links > 0],
          ].map(([label, val, warn]) => (
            <div key={label} className={`rounded-lg border p-2 ${warn ? 'border-rose-200 bg-rose-50' : 'border-zinc-100 bg-zinc-50'}`}>
              <p className={`text-lg font-bold tabular-nums ${warn ? 'text-rose-600' : 'text-zinc-800'}`}>{val}</p>
              <p className="text-[10px] text-zinc-500">{label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* サイドバーの表示（ロール単位） */}
      <NavVisibilityCard />

      {/* Trash retention */}      <div className="rounded-xl border border-zinc-200 bg-white shadow-sm p-4" data-testid="dms-admin-settings">
        <p className="text-sm font-bold text-zinc-800 flex items-center gap-1.5 mb-2">
          <Gear size={16} weight="duotone" className="text-zinc-500" /> ゴミ箱保持期間
        </p>
        <div className="flex items-center gap-2 flex-wrap">
          <Input type="number" min="1" max="3650" value={retention} onChange={(e) => setRetention(e.target.value)}
            className="h-9 w-28" data-testid="dms-retention-input" />
          <span className="text-xs text-zinc-500">日（超過分は手動パージで完全削除）</span>
          <Button size="sm" onClick={saveRetention} className="h-9 text-xs bg-teal-700 hover:bg-teal-800"
            data-testid="dms-retention-save">保存</Button>
          <Button size="sm" variant="outline" onClick={purge} className="h-9 text-xs text-rose-600 border-rose-300"
            data-testid="dms-purge-btn">期限切れをパージ</Button>
        </div>
      </div>

      {/* 取り込みキュー — 同時に走らせる AI 読み取りの件数 */}
      <div className="rounded-xl border border-zinc-200 bg-white shadow-sm p-4" data-testid="dms-admin-ingest-queue">
        <p className="text-sm font-bold text-zinc-800 flex items-center gap-1.5 mb-1">
          <Gear size={16} weight="duotone" className="text-sky-600" /> 取り込みの読み取りキュー
        </p>
        <p className="text-[11px] text-zinc-400 mb-3">
          一度に走らせる AI 読み取りの件数。増やすと速くなりますが、空の応答・JSONの崩れ・タイムアウトが増えます。
既定は「1人あたり1件」で、誰かの大量投入で他の人が待たされません。
          大量取り込みのときだけ、1人あたりの上限と同時実行数を上げてください。
        </p>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-zinc-600">同時実行数</span>
          <Input type="number" min="1" max="32" value={maxConc} onChange={(e) => setMaxConc(e.target.value)}
            className="h-9 w-20" data-testid="dms-ingest-concurrency-input" />
          <span className="text-xs text-zinc-600 ml-2">1人あたり</span>
          <Input type="number" min="1" max="32" value={perUser} onChange={(e) => setPerUser(e.target.value)}
            className="h-9 w-20" data-testid="dms-ingest-per-user-input" />
          <span className="text-xs text-zinc-600 ml-2">待機の上限</span>
          <Input type="number" min="5" max="240" value={queueTimeout} onChange={(e) => setQueueTimeout(e.target.value)}
            className="h-9 w-24" data-testid="dms-ingest-timeout-input" />
          <span className="text-xs text-zinc-500">分（超えた行は失敗にして再試行できるようにします）</span>
          <Button size="sm" onClick={saveRetention} className="h-9 text-xs bg-teal-700 hover:bg-teal-800"
            data-testid="dms-ingest-queue-save">保存</Button>
        </div>
      </div>

      {/* 待ち行列のライブ表示 + 取消（詰まりを再起動なしで外す） */}
      <IngestQueueCard />

      {/* Bulk folder maintenance (V4) */}
      <BulkOcrCard />

      {/* File adoption (Step 2) */}
      <AdoptionCard />

      {/* Feature Registry */}
      <div className="rounded-xl border border-zinc-200 bg-white shadow-sm p-4" data-testid="dms-admin-registry">
        <p className="text-sm font-bold text-zinc-800 flex items-center gap-1.5 mb-1">
          <ShieldCheck size={16} weight="duotone" className="text-teal-600" /> 機能レジストリ・依存関係グラフ
        </p>
        <p className="text-[11px] text-zinc-400 mb-3">
          安全必須機能はロックされ無効化できません。依存関係が満たされない切替はブロックされます。
        </p>
        {phases.map((ph) => (
          <div key={ph} className="mb-4">
            <p className="text-[11px] font-bold text-zinc-500 uppercase mb-1.5">{PHASE_LABEL[ph] || `Phase ${ph}`}</p>
            <div className="space-y-1">
              {features.filter((f) => f.phase === ph).map((f) => (
                <div key={f.id} className="flex items-center justify-between gap-3 rounded-lg border border-zinc-100 px-3 py-2"
                  data-testid={`dms-feature-${f.id}`}>
                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-zinc-800 flex items-center gap-1.5 flex-wrap">
                      {f.feature_name}
                      {f.is_safety_critical && (
                        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-zinc-800 text-white text-[9px] font-bold">
                          <LockKey size={9} weight="fill" /> ロック
                        </span>
                      )}
                      {!f.implemented && (
                        <span className="px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-400 text-[9px] font-bold">未実装</span>
                      )}
                    </p>
                    <p className="text-[10px] text-zinc-400 truncate">{f.description}</p>
                    {(f.required_dependencies || []).length > 0 && (
                      <p className="text-[9px] text-zinc-400">依存: {f.required_dependencies.join(', ')}</p>
                    )}
                  </div>
                  <Switch checked={!!f.is_enabled} disabled={f.is_safety_critical || !f.implemented}
                    onCheckedChange={(v) => toggle(f, v)}
                    data-testid={`dms-feature-toggle-${f.id}`} />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Workflow rules — admin visible, no hidden automation */}
      <WorkflowRulesSection />

      {/* Toggle history */}
      {(health.recent_feature_toggles || []).length > 0 && (
        <div className="rounded-xl border border-zinc-200 bg-white shadow-sm p-4" data-testid="dms-admin-toggle-history">
          <p className="text-sm font-bold text-zinc-800 mb-2">機能切替履歴</p>
          {health.recent_feature_toggles.map((t) => (
            <p key={t.id} className="text-[11px] text-zinc-500 py-0.5">
              <span className="font-mono text-zinc-400">{formatJST(t.at)}</span>{' '}
              {t.actor_name} — {t.detail?.feature} を {t.detail?.enabled ? '有効化' : '無効化'}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

const TRIGGER_LABELS = { on_upload: 'アップロード時', on_stamp: 'スタンプ時' };
const COND_LABELS = { source_type: '取得元', doc_type: '種別', folder_id: 'フォルダー', stamp: 'スタンプ' };

function WorkflowRulesSection() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try { setData(await dmsApi.workflowRules()); } catch { /* feature off */ }
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!data) return null;

  const toggleRule = async (r, active) => {
    try {
      await dmsApi.updateRule(r.id, {
        name: r.name, trigger_type: r.trigger_type, condition: r.condition || {},
        actions: r.actions, is_active: active, priority: r.priority ?? 100,
      });
      toast.success(`ルール「${r.name}」を${active ? '有効化' : '無効化'}しました`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || '更新に失敗しました'); }
  };

  const removeRule = async (r) => {
    try {
      await dmsApi.deleteRule(r.id);
      toast.success('ルールを削除しました');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || '削除に失敗しました'); }
  };

  const saveNew = async () => {
    if (!form.name.trim()) { toast.error('ルール名を入力してください'); return; }
    if (!form.actionValue.trim()) { toast.error('アクションの値を入力してください'); return; }
    const condition = {};
    if (form.condKey && form.condValue.trim()) condition[form.condKey] = form.condValue.trim();
    setSaving(true);
    try {
      await dmsApi.createRule({
        name: form.name.trim(), trigger_type: form.trigger, condition,
        actions: [{ type: form.actionType, value: form.actionValue.trim() }],
        is_active: true, priority: 100,
      });
      toast.success('ワークフロールールを作成しました');
      setForm(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || '作成に失敗しました'); }
    finally { setSaving(false); }
  };

  const ruleLine = (r, system) => (
    <div key={r.id} className="flex items-center justify-between gap-3 rounded-lg border border-zinc-100 px-3 py-2"
      data-testid={`dms-rule-${r.id}`}>
      <div className="min-w-0">
        <p className="text-xs font-semibold text-zinc-800 flex items-center gap-1.5">
          {r.name}
          {system && <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-zinc-800 text-white text-[9px] font-bold"><LockKey size={9} weight="fill" /> システム</span>}
        </p>
        <p className="text-[10px] text-zinc-400">
          {TRIGGER_LABELS[r.trigger_type] || r.trigger_type}
          {Object.entries(r.condition || {}).map(([k, v]) => ` · ${COND_LABELS[k] || k}=${v}`).join('')}
          {' → '}{(r.actions || []).map((a) => `${a.type === 'set_status' ? 'ステータス' : a.type === 'add_tag' ? 'タグ追加' : a.type}: ${a.value}`).join(', ')}
        </p>
      </div>
      {system ? (
        <span className="text-[10px] text-zinc-400 shrink-0">無効化不可</span>
      ) : (
        <span className="flex items-center gap-2 shrink-0">
          <Switch checked={!!r.is_active} onCheckedChange={(v) => toggleRule(r, v)}
            data-testid={`dms-rule-toggle-${r.id}`} />
          <button type="button" onClick={() => removeRule(r)} className="text-zinc-300 hover:text-rose-600"
            data-testid={`dms-rule-delete-${r.id}`}>
            <TrashSimple size={14} />
          </button>
        </span>
      )}
    </div>
  );

  return (
    <div className="rounded-xl border border-zinc-200 bg-white shadow-sm p-4" data-testid="dms-admin-rules">
      <div className="flex items-center justify-between mb-1">
        <p className="text-sm font-bold text-zinc-800 flex items-center gap-1.5">
          <GitBranch size={16} weight="duotone" className="text-violet-600" /> ワークフロールール
        </p>
        <Button size="sm" variant="outline" onClick={() => setForm({ name: '', trigger: 'on_upload', condKey: 'source_type', condValue: '', actionType: 'set_status', actionValue: '' })}
          className="h-8 text-xs" data-testid="dms-rule-new-btn">
          <Plus size={13} className="mr-1" /> ルール追加
        </Button>
      </div>
      <p className="text-[11px] text-zinc-400 mb-3">
        隠れた自動処理は作りません — 全ルールはここに表示され、適用は書類の履歴に記録されます。
      </p>
      <div className="space-y-1">
        {data.system_rules.map((r) => ruleLine(r, true))}
        {data.rules.map((r) => ruleLine(r, false))}
        {data.rules.length === 0 && <p className="text-[11px] text-zinc-400 px-1 py-2">カスタムルールはまだありません</p>}
      </div>

      {form && (
        <div className="mt-3 rounded-lg border border-violet-200 bg-violet-50/50 p-3 space-y-2" data-testid="dms-rule-form">
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="ルール名（例: スキャンは要分類へ）" maxLength={120} className="h-8 text-xs" data-testid="dms-rule-name" />
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <select value={form.trigger} onChange={(e) => setForm({ ...form, trigger: e.target.value })}
              className="h-8 rounded-md border border-zinc-300 bg-white px-2 text-xs" data-testid="dms-rule-trigger">
              <option value="on_upload">アップロード時</option>
              <option value="on_stamp">スタンプ時</option>
            </select>
            <select value={form.condKey} onChange={(e) => setForm({ ...form, condKey: e.target.value })}
              className="h-8 rounded-md border border-zinc-300 bg-white px-2 text-xs" data-testid="dms-rule-cond-key">
              <option value="source_type">条件: 取得元</option>
              <option value="doc_type">条件: 種別</option>
              <option value="stamp">条件: スタンプ</option>
            </select>
            <Input value={form.condValue} onChange={(e) => setForm({ ...form, condValue: e.target.value })}
              placeholder="条件の値（例: scan）" className="h-8 text-xs" data-testid="dms-rule-cond-value" />
            <select value={form.actionType} onChange={(e) => setForm({ ...form, actionType: e.target.value })}
              className="h-8 rounded-md border border-zinc-300 bg-white px-2 text-xs" data-testid="dms-rule-action-type">
              <option value="set_status">ステータス設定</option>
              <option value="add_tag">タグ追加</option>
            </select>
          </div>
          <Input value={form.actionValue} onChange={(e) => setForm({ ...form, actionValue: e.target.value })}
            placeholder={form.actionType === 'set_status' ? 'ステータス値（例: needs_classification）' : 'タグ名（例: 製造用）'}
            className="h-8 text-xs" data-testid="dms-rule-action-value" />
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="outline" onClick={() => setForm(null)} className="h-8 text-xs" data-testid="dms-rule-cancel">キャンセル</Button>
            <Button size="sm" onClick={saveNew} disabled={saving} className="h-8 text-xs bg-teal-700 hover:bg-teal-800" data-testid="dms-rule-save">
              {saving ? '作成中...' : '作成'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
